import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  Zap,
  Banknote,
  PhoneCall,
  Monitor,
  CheckSquare,
  CreditCard,
  Store,
  ShoppingBag,
  Building2,
  Rocket,
  Stethoscope,
  Car,
  Activity,
  PieChart,
  Receipt,
  ScrollText,
  Lock,
} from "lucide-react";
import Counter from "../components/Counter";

const IMG = {
  hero: "https://images.unsplash.com/photo-1777565528494-d186f88e0e68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwYnVpbGRpbmclMjBhYnN0cmFjdCUyMG1pbmltYWxpc218ZW58MHx8fHwxNzc4NjgxNzU0fDA&ixlib=rb-4.1.0&q=85",
  dashboard:
    "https://images.unsplash.com/photo-1686061592689-312bbfb5c055?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGRhc2hib2FyZCUyMGdyYXBoJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzc4NjgxNzY4fDA&ixlib=rb-4.1.0&q=85",
};

export default function HomePage() {
  return (
    <>
      <Hero />
      <PartnersMarquee />
      <Features />
      <StatsStrip />
      <Dashboard />
      <Industries />
      <Clients />
      <FinalCTA />
    </>
  );
}

/* ---------------------------------- HERO ---------------------------------- */

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-ink-900 bg-white" data-testid="hero-section">
      {/* Background image (greyscale, very subtle) */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.08] grayscale"
        style={{ backgroundImage: `url(${IMG.hero})` }}
      />
      <div aria-hidden className="pointer-events-none absolute inset-0 paper-grid opacity-60" />

      <div className="container-wide relative z-10 grid items-end gap-10 py-20 lg:grid-cols-12 lg:gap-12 lg:py-32">
        <div className="lg:col-span-8">
          <h1
            className="font-display text-[15vw] font-black uppercase leading-[0.85] tracking-tighter text-ink-900 sm:text-[12vw] lg:text-[10.5vw]"
            data-testid="hero-title"
          >
            Payments
            <br />
            Made <span className="text-accent">Simple.</span>
            <br />
            <span className="text-outline">The Way It</span>
            <br />
            Should Be.
          </h1>

          <div className="mt-10 grid gap-8 sm:grid-cols-[1fr_auto] sm:items-end">
            <p className="max-w-xl font-mono text-base leading-relaxed text-ink-500 sm:text-lg">
              Tailored payment solutions built for your business. America's #1
              payment processor for coaches, consultants, creators, digital
              marketers, and publishers.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/apply" className="btn-primary" data-testid="hero-cta-primary">
                Get Processing Now <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact-us" className="btn-secondary" data-testid="hero-cta-secondary">
                Free Consultation <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        {/* Sidecard — terminal-style */}
        <aside className="relative lg:col-span-4">
          <div className="border border-ink-900 bg-white">
            <div className="flex items-center justify-between border-b border-ink-900 bg-ink-900 px-4 py-2.5 text-white">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 bg-accent" />
                <span className="font-mono text-[10px] uppercase tracking-ultra">
                  TERMINAL / LIVE
                </span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-ultra text-white/60">
                v2026.01
              </span>
            </div>
            <div className="space-y-5 p-6">
              <Row k="Approval rate" v="98.7%" />
              <div className="rule-light" />
              <Row k="Avg. settle" v="T + 1" />
              <div className="rule-light" />
              <Row k="Chargeback" v="0.42%" />
              <div className="rule-light" />
              <Row k="Uptime" v="99.99%" />
              <div className="rule-light" />
              <Row k="Latency" v="148ms" />
              <div className="mt-6 grid grid-cols-3 gap-2 pt-2">
                {["VISA", "MC", "AMEX", "DISC", "ACH", "JCB"].map((x) => (
                  <div
                    key={x}
                    className="border border-ink-900 px-2 py-2 text-center font-mono text-[10px] font-semibold uppercase tracking-wide2"
                  >
                    {x}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="absolute -bottom-4 -right-3 hidden border border-ink-900 bg-accent px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-ultra text-white sm:block">
            Instant Approval
          </div>
        </aside>
      </div>
    </section>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="font-mono text-[11px] uppercase tracking-ultra text-ink-500">{k}</span>
      <span className="font-display text-2xl font-black tracking-tighter text-ink-900">{v}</span>
    </div>
  );
}

/* ----------------------------- PARTNERS MARQUEE --------------------------- */

function PartnersMarquee() {
  const items = [
    "VISA",
    "MASTERCARD",
    "AMEX",
    "DISCOVER",
    "JCB",
    "UNIONPAY",
    "ACH",
    "APPLE PAY",
    "GOOGLE PAY",
    "PIX",
    "SEPA",
    "iDEAL",
    "ALIPAY",
    "WECHAT",
  ];
  const loop = [...items, ...items];
  return (
    <section className="border-b border-ink-900 bg-white py-6" data-testid="partners-marquee">
      <div className="container-wide flex items-center justify-between border-b border-ink-100 pb-4">
        <span className="eyebrow-accent">
          <span className="h-2 w-2 bg-accent" /> Partners & Integrations
        </span>
        <span className="hidden font-mono text-[11px] uppercase tracking-ultra text-ink-500 sm:inline">
          Unlimited Processing Solutions
        </span>
      </div>
      <div className="overflow-hidden pt-6">
        <div className="marquee-track gap-12">
          {loop.map((x, i) => (
            <span
              key={`${x}-${i}`}
              className="flex shrink-0 items-center gap-12 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl"
            >
              <span className="text-ink-900">{x}</span>
              <span className="text-accent">✱</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- FEATURES -------------------------------- */

const FEATURES = [
  {
    n: "01",
    title: "No Hidden Fees",
    body: "Simple, straightforward pricing. What we quote is what you pay — no surprises, no tricks, no fine print.",
    icon: ScrollText,
  },
  {
    n: "02",
    title: "Free, Fast Setup",
    body: "Now offering instant approval. Get onboarded and processing payments in hours, not weeks.",
    icon: Zap,
  },
  {
    n: "03",
    title: "Online Check Acceptance",
    body: "Free savings proposals. Accept checks by web, phone, and fax with full reporting included.",
    icon: CheckSquare,
  },
  {
    n: "04",
    title: "Next-Day Funding",
    body: "Visa, Mastercard, Discover, and Amex deposited the next business day. Cash flow you can plan around.",
    icon: Banknote,
  },
  {
    n: "05",
    title: "Virtual Terminal Included",
    body: "Phone orders, in-person sales, and mail-order. One terminal, every channel, zero extra cost.",
    icon: Monitor,
  },
  {
    n: "06",
    title: "Industry-Leading Support",
    body: "24 / 7 / 365 live, toll-free phone support staffed by humans who know your industry inside-out.",
    icon: PhoneCall,
  },
];

function Features() {
  return (
    <section id="solutions" className="border-b border-ink-900 bg-white" data-testid="features-section">
      <div className="container-wide grid items-end gap-10 py-20 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-7">
          <span className="eyebrow-accent">
            <span className="h-2 w-2 bg-accent" /> // What's Included
          </span>
          <h2 className="section-mega mt-6">
            The All-Inclusive
            <br />
            <span className="text-accent">Merchant Solution.</span>
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="body-mono max-w-md">
            One contract, every channel, no upcharges. Everything below is
            standard with every UMG merchant account — across retail, online,
            and high-risk verticals.
          </p>
        </div>
      </div>

      {/* Grid with shared 1px black borders */}
      <div className="border-t border-ink-900">
        <div className="container-wide">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(({ n, title, body, icon: Icon }, i) => (
              <article
                key={n}
                data-testid={`feature-card-${n}`}
                className={`group relative border-ink-900 p-10 transition-colors duration-200 hover:bg-ink-900 hover:text-white ${
                  // right border for all except last col
                  ((i + 1) % 3 !== 0 ? "lg:border-r" : "") + " "
                } ${
                  ((i + 1) % 2 !== 0 ? "md:border-r lg:border-r" : "md:border-r-0")
                } border-b`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-xs uppercase tracking-ultra text-ink-500 group-hover:text-white/60">
                    {n}
                  </span>
                  <Icon className="h-6 w-6 text-ink-900 group-hover:text-accent" />
                </div>
                <h3 className="mt-10 font-display text-2xl font-black uppercase leading-[0.95] tracking-tighter sm:text-3xl">
                  {title}
                </h3>
                <p className="mt-5 font-mono text-sm leading-relaxed text-ink-500 group-hover:text-white/70">
                  {body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- STATS STRIP ------------------------------ */

function StatsStrip() {
  return (
    <section className="relative overflow-hidden border-b border-ink-900 bg-ink-900 text-white" data-testid="stats-section">
      <div aria-hidden className="pointer-events-none absolute inset-0 paper-grid-dark opacity-50" />
      <div className="container-wide relative z-10 grid gap-12 py-20 lg:grid-cols-12 lg:py-28">
        <div className="lg:col-span-4">
          <span className="eyebrow-inverse">
            <span className="h-2 w-2 bg-accent" /> By the Numbers
          </span>
          <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[0.9] tracking-tighter sm:text-5xl lg:text-6xl">
            Numbers don't
            <br />
            <span className="text-accent">negotiate.</span>
          </h2>
          <p className="body-mono mt-6 text-white/60">
            Cumulative performance across our merchant network — verified by
            our acquiring partners and updated daily.
          </p>
        </div>

        <div className="lg:col-span-8">
          <div className="grid grid-cols-2 lg:grid-cols-4">
            <Stat number={<Counter end={12500} suffix="+" />} label="Satisfied Merchants" />
            <Stat number={<><Counter end={48} prefix="$" suffix="M" /></>} label="Saved Fees / Year" />
            <Stat number={<><Counter end={6} prefix="$" suffix="B+" /></>} label="Processed Payments" />
            <Stat number={<Counter end={20} suffix="+" />} label="Acquiring Banks" last />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ number, label, last = false }) {
  return (
    <div className={`border-l border-white/20 px-6 py-2 ${last ? "border-r" : ""}`}>
      <div className="font-display text-5xl font-black tracking-tighter text-white sm:text-6xl">
        {number}
      </div>
      <div className="mt-4 font-mono text-[11px] uppercase tracking-ultra text-white/50">
        {label}
      </div>
    </div>
  );
}

/* -------------------------------- DASHBOARD ------------------------------- */

const TILES = [
  { t: "Dashboard Overview", icon: Activity },
  { t: "Reporting & Analytics", icon: PieChart },
  { t: "Transaction History", icon: ScrollText },
  { t: "Product & Purchase Analytics", icon: ShoppingBag },
  { t: "Fraud & Risk Protection", icon: Lock },
  { t: "Orders & Invoicing", icon: Receipt },
];

function Dashboard() {
  return (
    <section className="border-b border-ink-900 bg-white" data-testid="dashboard-section">
      <div className="container-wide grid gap-12 py-20 lg:grid-cols-12 lg:py-28">
        <div className="lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
          <span className="eyebrow-accent">
            <span className="h-2 w-2 bg-accent" /> Additional Features
          </span>
          <h2 className="section-title mt-6">
            Get Paid in
            <br />
            Every Way.
            <br />
            <span className="text-outline">Online.</span>
            <br />
            <span className="text-outline">In Person.</span>
            <br />
            <span className="text-accent">Everywhere.</span>
          </h2>
          <p className="body-mono mt-6 max-w-md">
            A single operating layer for your entire payments stack — reporting,
            fraud, invoicing, and analytics under one roof.
          </p>
          <div className="mt-8">
            <Link to="/contact-us" className="btn-primary" data-testid="dashboard-cta">
              See the Platform <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 sm:grid-cols-2">
            {TILES.map(({ t, icon: Icon }, i) => (
              <div
                key={t}
                data-testid={`dashboard-tile-${i}`}
                className={`group relative border-b border-ink-900 p-8 transition-colors duration-200 hover:bg-ink-50 ${
                  (i + 1) % 2 !== 0 ? "sm:border-r" : ""
                } ${i >= TILES.length - 2 ? "sm:border-b-0" : ""}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-ultra text-ink-400">
                    0{i + 1} / Module
                  </span>
                  <Icon className="h-5 w-5 text-ink-900 group-hover:text-accent" />
                </div>
                <h3 className="mt-8 font-display text-xl font-black uppercase leading-tight tracking-tighter sm:text-2xl">
                  {t}
                </h3>
                <div className="mt-5 h-12 border border-ink-100 bg-grid-light bg-[length:8px_8px]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- INDUSTRIES ------------------------------- */

const INDUSTRIES = [
  { name: "Retail", icon: Store, n: "01" },
  { name: "E-Commerce", icon: ShoppingBag, n: "02" },
  { name: "B2B", icon: Building2, n: "03" },
  { name: "Startups", icon: Rocket, n: "04" },
  { name: "TeleMed", icon: Stethoscope, n: "05" },
  { name: "Peptides", icon: Activity, n: "06" },
  { name: "Auto Dealership", icon: Car, n: "07" },
];

function Industries() {
  return (
    <section id="industries" className="border-b border-ink-900 bg-white" data-testid="industries-section">
      <div className="container-wide grid items-end gap-10 py-20 lg:grid-cols-12 lg:py-24">
        <div className="lg:col-span-8">
          <span className="eyebrow-accent">
            <span className="h-2 w-2 bg-accent" /> Industries
          </span>
          <h2 className="section-mega mt-6">
            We work with
            <br />
            <span className="text-outline">99%</span> of industries
            <br />
            <span className="text-accent">including:</span>
          </h2>
        </div>
        <div className="lg:col-span-4">
          <p className="body-mono">
            From boutique retail to high-velocity SaaS and regulated TeleMed,
            our risk teams underwrite every vertical — even the ones other
            processors won't touch.
          </p>
          <Link
            to="/contact-us"
            className="mt-6 inline-flex items-center gap-2 border-b border-ink-900 pb-1 font-mono text-xs uppercase tracking-wide2 hover:text-accent hover:border-accent"
            data-testid="industries-see-more"
          >
            See more →
          </Link>
        </div>
      </div>

      <div className="border-t border-ink-900">
        <div className="container-wide">
          <ul className="divide-y divide-ink-900 border-b border-ink-900">
            {INDUSTRIES.map(({ name, icon: Icon, n }) => (
              <li
                key={name}
                data-testid={`industry-row-${n}`}
                className="group flex items-center justify-between gap-6 py-8 transition-colors duration-200 hover:bg-accent hover:text-white sm:py-10"
              >
                <div className="container-wide flex items-center justify-between gap-6">
                  <div className="flex items-baseline gap-6 sm:gap-10">
                    <span className="font-mono text-xs uppercase tracking-ultra text-ink-400 group-hover:text-white/70">
                      {n}
                    </span>
                    <span className="font-display text-4xl font-black uppercase leading-none tracking-tighter sm:text-5xl lg:text-6xl">
                      {name}
                    </span>
                  </div>
                  <div className="flex items-center gap-6">
                    <Icon className="hidden h-6 w-6 sm:block" />
                    <ArrowUpRight className="h-6 w-6 transition-transform group-hover:rotate-[-15deg]" />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* --------------------------------- CLIENTS -------------------------------- */

function Clients() {
  const brands = [
    "ASTRA",
    "BRAVO",
    "RIDE",
    "PLANET",
    "FORTIS",
    "NORTHWIND",
    "BLOCKHAUS",
    "MERIDIAN",
  ];
  return (
    <section className="border-b border-ink-900 bg-white" data-testid="clients-section">
      <div className="container-wide py-20 lg:py-24">
        <div className="flex items-end justify-between border-b border-ink-900 pb-6">
          <span className="eyebrow-accent">
            <span className="h-2 w-2 bg-accent" /> Our Clients
          </span>
          <h2 className="font-display text-2xl font-black uppercase tracking-tighter sm:text-3xl">
            We Proudly Represent
          </h2>
        </div>

        <div className="grid grid-cols-2 border-b border-ink-900 sm:grid-cols-4">
          {brands.map((b, i) => (
            <div
              key={b}
              className={`flex items-center justify-center border-ink-900 py-12 ${
                (i + 1) % 4 !== 0 ? "sm:border-r" : ""
              } ${(i + 1) % 2 !== 0 ? "border-r" : ""} ${
                i < brands.length - 4 ? "border-b sm:border-b" : ""
              }`}
            >
              <span className="font-display text-2xl font-black uppercase tracking-tighter text-ink-900/80 sm:text-3xl">
                {b}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- FINAL CTA ------------------------------- */

function FinalCTA() {
  return (
    <section className="relative overflow-hidden bg-accent text-white" data-testid="final-cta">
      <div aria-hidden className="pointer-events-none absolute inset-0 paper-grid-dark opacity-50" />
      <div className="container-wide relative z-10 grid items-center gap-12 py-20 lg:grid-cols-12 lg:py-28">
        <div className="lg:col-span-8">
          <span className="font-mono text-[11px] uppercase tracking-ultra text-white/80">
            // FINAL CALL
          </span>
          <h2 className="mt-6 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl lg:text-7xl">
            Unlock your cost
            <br />
            savings and
            <br />
            custom solutions
            <br />
            <span className="text-outline-white">now.</span>
          </h2>
        </div>
        <div className="lg:col-span-4">
          <p className="font-mono text-base leading-relaxed text-white/90">
            Free, no-obligation savings proposal. Most merchants discover
            we're cheaper by the second statement comparison.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact-us" className="btn-inverse" data-testid="final-cta-button">
              Get Started <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/about-us" className="btn-ghost-dark">
              About UMG
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
